import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  ScrollView,
  SafeAreaView,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { COLORS, APP_CONSTANTS, strings } from '@/constants/residents';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
import { AuthStackParamList } from '@/navigation/residents/mobile/AuthNavigator';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useToast } from '@/contexts/admin/ToastContext';
import { showAlert } from '@/utils/common/alert';

type AccountVerificationRouteProp = RouteProp<AuthStackParamList, 'AccountVerification'>;
type AccountVerificationNavigationProp = StackNavigationProp<AuthStackParamList, 'AccountVerification'>;

export const AccountVerificationScreen: React.FC = () => {
  const navigation = useNavigation<AccountVerificationNavigationProp>();
  const route = useRoute<AccountVerificationRouteProp>();
  const [isResending, setIsResending] = useState(false);
  const { showSuccess, showError } = useToast();
  
  const email = route.params?.email || '';
  const isSignup = route.params?.isSignup || false;

  const handleResendEmail = async () => {
    if (!email) {
      if (Platform.OS === 'web') {
        showError('No email address found.');
      } else {
        showAlert('Error', 'No email address found.');
      }
      return;
    }

    setIsResending(true);
    try {
      // Use sendVerification for both signup and forgot password (matching iOS)
      const response = await AuthAPI.sendVerification(email);
      
      if (response.success) {
        if (Platform.OS === 'web') {
          showSuccess('Verification email successfully sent');
        } else {
          showAlert('Email Sent', 'Verification email successfully sent');
        }
      } else {
        const errorMsg = response.message || 'Failed to resend verification email.';
        if (Platform.OS === 'web') {
          showError(errorMsg);
        } else {
          showAlert('Error', errorMsg);
        }
      }
    } catch (error: any) {
      const errorMsg = error.message || 'An error occurred while resending the verification email.';
      if (Platform.OS === 'web') {
        showError(errorMsg);
      } else {
        showAlert('Error', errorMsg);
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleGoBack = () => {
    // Prefer native back if possible to respect where this screen was opened from
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }

    // If no history, and this came from auth flow, navigate to Login
    if (!isSignup) {
      navigation.navigate('Login');
      return;
    }

    // Last-resort: no-op to avoid unhandled navigation warnings
  };

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView 
        style={styles.keyboardContainer} 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        {/* Navigation Bar with Back Button */}
        <View style={styles.navigationBar}>
          <TouchableOpacity 
            style={styles.backButton} 
            onPress={handleGoBack}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Text style={styles.backArrow}>←</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={styles.scrollContainer}>
          {/* Content Container - iOS layout: Icon, Title, Description, Buttons */}
          <View style={styles.contentContainer}>
            {/* Mail Icon at top */}
            <View style={styles.iconContainer}>
              <SVG_COMPONENTS.SEND_INVITATION 
                width={64} 
                height={64} 
                style={styles.mailIcon}
              />
            </View>

            {/* Title */}
            <Text style={styles.titleLabel}>{strings.verification.accountVerifyTitle}</Text>
            
            {/* Description */}
            <Text style={styles.subtitleLabel}>
              {strings.verification.accountVerifyDescription}
            </Text>

            {/* Button Container */}
            <View style={styles.buttonContainer}>
            {/* Resend Email Button */}
            <TouchableOpacity
              style={[styles.button, styles.resendButton]}
              onPress={handleResendEmail}
              disabled={isResending}
            >
              {isResending ? (
                <ActivityIndicator color={COLORS.TEXT_PRIMARY} size="small" />
              ) : (
                <Text style={styles.resendButtonText}>{strings.verification.accountVerifyResend}</Text>
              )}
            </TouchableOpacity>

            {/* Go Back Button */}
            <TouchableOpacity
              style={[styles.button, styles.goBackButton]}
              onPress={handleGoBack}
              disabled={isResending}
            >
              <Text style={styles.goBackButtonText}>{strings.verification.accountVerifyCancel}</Text>
            </TouchableOpacity>
          </View>
        </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.BACKGROUND_PRIMARY,
  },
  keyboardContainer: {
    flex: 1,
  },
  navigationBar: {
    height: 44,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    backgroundColor: COLORS.BACKGROUND_PRIMARY,
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backArrow: {
    fontSize: 24,
    color: COLORS.TEXT_PRIMARY,
    fontWeight: '400',
  },
  scrollContainer: {
    flexGrow: 1,
  },
  contentContainer: {
    flex: 1,
    backgroundColor: COLORS.BACKGROUND_PRIMARY,
    paddingHorizontal: 32,
    paddingTop: 48,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  iconContainer: {
    marginBottom: 24,
    alignItems: 'center',
  },
  titleLabel: {
    fontSize: 20,
    fontWeight: '900',
    color: COLORS.TEXT_PRIMARY,
    textAlign: 'center',
    marginBottom: 16,
    fontFamily: Platform.select({
      ios: 'Avenir-Black',
      android: 'sans-serif-black',
      default: 'System',
    }),
  },
  subtitleLabel: {
    fontSize: 13,
    color: COLORS.TEXT_PRIMARY,
    textAlign: 'center',
    lineHeight: 18,
    marginBottom: 48,
    fontFamily: Platform.select({
      ios: 'Avenir-Book',
      android: 'sans-serif',
      default: 'System',
    }),
  },
  mailIcon: {
    opacity: 0.6,
  },
  buttonContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  button: {
    flex: 1,
    borderRadius: APP_CONSTANTS.BUTTON_CORNER_RADIUS / 2,
    paddingVertical: 12,
    paddingHorizontal: 24,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  resendButton: {
    backgroundColor: COLORS.BACKGROUND_SECONDARY,
    borderWidth: 1,
    borderColor: COLORS.BUTTON_PRIMARY,
  },
  resendButtonText: {
    color: COLORS.BUTTON_PRIMARY,
    fontSize: 13,
    fontWeight: '500',
    fontFamily: Platform.select({
      ios: 'Avenir-Medium',
      android: 'sans-serif-medium',
      default: 'System',
    }),
  },
  goBackButton: {
    backgroundColor: COLORS.BUTTON_PRIMARY,
  },
  goBackButtonText: {
    color: COLORS.TEXT_WHITE,
    fontSize: 13,
    fontWeight: '500',
    fontFamily: Platform.select({
      ios: 'Avenir-Medium',
      android: 'sans-serif-medium',
      default: 'System',
    }),
  },
});
